import axios from 'axios';

// Use the correct API URL based on the environment (matches HomePage's pattern).
export const API_URL = window.location.hostname === 'arcade.abaj.ai'
  ? 'https://arcade.abaj.ai/api'
  : process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TOKEN_KEY = 'arcade_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Set the token everywhere: localStorage, this instance, and the GLOBAL axios
// defaults so that legacy pages importing `axios` directly also send the Bearer.
export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

// If a token is already present at module load, prime the global axios defaults
// so legacy pages carry it on their very first request.
const bootToken = getToken();
if (bootToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
}

const api = axios.create({ baseURL: API_URL });

// Attach the Bearer token to every request.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Requests to the auth endpoints themselves should surface their own errors
// (bad credentials / duplicate email) rather than triggering a redirect loop.
const isAuthEndpoint = (url = '') =>
  /\/api\/auth\/(login|register)$/.test(url) || /\/auth\/(login|register)$/.test(url);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (status === 401 && !isAuthEndpoint(url)) {
      clearToken();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    } else if (status === 402) {
      if (window.location.pathname !== '/subscribe') {
        window.location.assign('/subscribe');
      }
    }
    return Promise.reject(error);
  }
);

// Named helpers -------------------------------------------------------------

export const register = (email, password) =>
  api.post('/auth/register', { email, password }).then((r) => r.data);

export const login = (email, password) =>
  api.post('/auth/login', { email, password }).then((r) => r.data);

// Backend returns { user, access } as siblings; flatten into a single user
// object so consumers can read user.email / user.isAdmin / user.access.active
// uniformly.
export const getMe = () =>
  api.get('/auth/me').then((r) => {
    const { user, access } = r.data;
    return { ...user, access };
  });

export const createCheckoutSession = () =>
  api.post('/billing/create-checkout-session').then((r) => r.data);

export const syncCheckout = (sessionId) =>
  api.post('/billing/sync-checkout', { sessionId }).then((r) => r.data);

export default api;
